import React, { useState, useCallback, useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import annotationPlugin from 'chartjs-plugin-annotation';
import zoomPlugin from 'chartjs-plugin-zoom';
import { buildBackgroundAnnotations, buildThresholdLines, buildActivityAnnotations } from './chartAnnotations';
import { buildPmData, buildPmOptions, pmPanelStats, pmYAxisMax } from './pmChartOptions';
import { PmStatsPanel, QuickAnnotPopover, PmChartFooter } from './_pmChartBlocks';
import useChartZoomWindow from './useChartZoomWindow';
import AnalyzeZoomButton from './AnalyzeZoomButton';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  annotationPlugin,
  zoomPlugin
);

/**
 * PMChart — PM1, PM2.5, PM10 de la session (ou de la période) avec seuils,
 * annotations d'activité et zoom. Configuration Chart.js : pmChartOptions.js ;
 * zoom et « Analyser la vue zoomée » : useChartZoomWindow.
 */
const PMChart = ({ readings, annotations = [], darkMode = false, onQuickAnnotate, onAnalyzeZoom }) => {
  const { t } = useTranslation();
  const [quickAnnot, setQuickAnnot] = useState(null); // { x, y, timestamp, pm25 }
  const zoom = useChartZoomWindow({ readings, onAnalyzeZoom, panModifierKey: 'shift', wheelSpeed: 0.1, limitToData: true });
  const chartRef = zoom.chartRef;

  const handleChartClick = useCallback((event) => {
    if (!onQuickAnnotate || !chartRef.current) return;
    const chart = chartRef.current;
    const rect  = chart.canvas.getBoundingClientRect();
    const xPos  = event.clientX - rect.left;
    const yPos  = event.clientY - rect.top;
    const elements = chart.getElementsAtEventForMode(event.nativeEvent || event,
      'nearest', { intersect: false }, false);
    if (!elements.length) { setQuickAnnot(null); return; }
    const idx = elements[0].index;
    const reading = readings[idx];
    if (!reading) return;
    setQuickAnnot({
      x: xPos, y: Math.max(40, yPos - 60),
      timestamp: reading.timestamp,
      pm25: reading.pm2_5
    });
  }, [onQuickAnnotate, readings, chartRef]);

  // Statistiques par PM — mémoïsées : readings peut contenir plusieurs milliers
  // de points, recalculer à chaque render (tooltip hover, zoom, annotation)
  // coûte cher. Hook placé avant l'early-return pour l'ordre stable des hooks.
  const { pm1Stats, pm25Stats, pm10Stats } = useMemo(() => pmPanelStats(readings), [readings]);

  if (!readings || readings.length === 0) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '40px',
        color: darkMode ? '#E8EDF2' : '#4A5568'
      }}>
        {t('charts.empty.generic')}
      </div>
    );
  }

  // Masquer les PM sans données réelles (tout à zéro)
  const visible = { pm1: pm1Stats.max > 0, pm25: pm25Stats.max > 0, pm10: pm10Stats.max > 0 };
  const maxPM25 = Math.max(...readings.map(r => r.pm2_5 || 0));
  const yAxisMax = pmYAxisMax(readings);

  // Zones de fond des seuils PM2.5 (adaptatif), lignes de seuil, annotations d'activité.
  const chartAnnotations = {
    ...Object.fromEntries(buildBackgroundAnnotations(yAxisMax, darkMode, maxPM25).map((a, i) => [`bg_${i}`, a])),
    ...Object.fromEntries(buildThresholdLines(darkMode, maxPM25, t).map((a, i) => [`thr_${i}`, a])),
    ...Object.fromEntries(buildActivityAnnotations(annotations, readings, yAxisMax, darkMode, t).map((a, i) => [`act_${i}`, a])),
  };

  const data = buildPmData(readings, visible, darkMode);
  const options = buildPmOptions({ readings, annotations: chartAnnotations, yAxisMax, zoom: zoom.zoomOptions, darkMode, t });

  const buttonStyle = (background) => ({
    padding: '6px 12px', background, color: '#fff', border: 'none',
    borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
  });

  return (
    <div>
      {/* Stats Panel - Afficher uniquement les PM avec données */}
      <PmStatsPanel darkMode={darkMode} entries={[
        visible.pm1 && { label: 'PM1', stats: pm1Stats },
        visible.pm25 && { label: 'PM2.5', stats: pm25Stats },
        visible.pm10 && { label: 'PM10', stats: pm10Stats },
      ].filter(Boolean)} />

      {/* Boutons au-dessus du graphique */}
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginBottom: '8px' }}>
        <AnalyzeZoomButton zoom={zoom} />
        <button
          onClick={() => {
            if (chartRef.current) {
              const url = chartRef.current.toBase64Image();
              const link = document.createElement('a');
              link.download = `ekynair-pm-chart-${new Date().toISOString().split('T')[0]}.png`;
              link.href = url;
              link.click();
            }
          }}
          style={buttonStyle('#10B981')}
        >
          PNG
        </button>
        <button onClick={zoom.resetZoom} style={buttonStyle('#1B77F1')}>
          {t('common.zoom.reset')}
        </button>
      </div>

      {/* Chart Container — height fixe sur wrapper, 100% sur inner pour que Chart.js remonte correctement */}
      <div style={{ position: 'relative', height: '400px' }}>
        <div style={{ position: 'relative', height: '100%' }} onClick={handleChartClick}>
          <Line ref={chartRef} data={data} options={options} />
          {quickAnnot && onQuickAnnotate && (
            <QuickAnnotPopover quickAnnot={quickAnnot} darkMode={darkMode}
              onAdd={() => { onQuickAnnotate(quickAnnot.timestamp); setQuickAnnot(null); }}
              onClose={() => setQuickAnnot(null)} />
          )}
        </div>
      </div>

      <PmChartFooter darkMode={darkMode} />
    </div>
  );
};

export default React.memo(PMChart, (prev, next) =>
  prev.readings === next.readings &&
  prev.annotations === next.annotations &&
  prev.darkMode === next.darkMode &&
  prev.onAnalyzeZoom === next.onAnalyzeZoom
);
